import {
  Alert,
  Box,
  Button,
  Collapse,
  Group,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconAlertTriangle, IconChevronDown, IconChevronRight, IconMapPin } from '@tabler/icons-react';
import { useEffect } from 'react';
import { UsStateSelect } from '@/components/UsStateSelect';
import { CLIENT_LOCATION_SOURCE_LABELS } from '@/lib/sessionLocation/constants';
import type { ClientLocationConfirmation } from '@/lib/sessionLocation/types';
import {
  clientLocationConfirmationFormSchema,
  type ClientLocationConfirmationFormInput,
} from '@/schemas/client-location-confirmation.schema';
import { sessionSidebarComboboxProps } from '../lib/sessionSidebarComboboxProps';

const EMPTY_VALUES: ClientLocationConfirmationFormInput = {
  street: '',
  city: '',
  state: '',
  postalCode: '',
  notAtUsualAddress: false,
  attestationAccepted: true,
};

function formatConfirmedAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export interface ParticipantSessionLocationSectionProps {
  patientId: string;
  confirmation: ClientLocationConfirmation | null;
  appliesToThisParticipant: boolean;
  loading?: boolean;
  saving?: boolean;
  saveError?: string | null;
  onSave: (patientId: string, values: ClientLocationConfirmationFormInput) => Promise<void>;
}

export function ParticipantSessionLocationSection({
  patientId,
  confirmation,
  appliesToThisParticipant,
  loading = false,
  saving = false,
  saveError,
  onSave,
}: ParticipantSessionLocationSectionProps): JSX.Element {
  const hasConfirmation = appliesToThisParticipant && confirmation != null;
  const [opened, { toggle, open }] = useDisclosure(hasConfirmation);

  const form = useForm<ClientLocationConfirmationFormInput>({
    initialValues: EMPTY_VALUES,
    validate: zod4Resolver(clientLocationConfirmationFormSchema),
  });

  useEffect(() => {
    if (hasConfirmation && confirmation) {
      form.setValues({
        street: confirmation.street,
        city: confirmation.city,
        state: confirmation.state,
        postalCode: confirmation.postalCode,
        notAtUsualAddress: confirmation.notAtUsualAddress,
        attestationAccepted: true,
      });
      open();
    } else {
      form.setValues(EMPTY_VALUES);
    }
  }, [confirmation, hasConfirmation]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = hasConfirmation
    ? `${confirmation!.city}, ${confirmation!.state}`
    : loading
      ? 'Loading…'
      : 'Not confirmed yet';

  return (
    <Box
      onPointerDownCapture={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <UnstyledButton onClick={toggle} w="100%" aria-expanded={opened}>
        <Group justify="space-between" wrap="nowrap" gap="xs" mb={opened ? 6 : 0}>
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
            <IconMapPin size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" fw={600} c="dimmed">
              Session location
            </Text>
          </Group>
          <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text size="xs" c="gray.7" truncate>
              {summary}
            </Text>
            {opened ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        <Stack gap="xs" pt={4}>
          {loading ? (
            <Text size="xs" c="dimmed">
              Waiting for client location confirmation.
            </Text>
          ) : null}

          {hasConfirmation ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Confirmed at: {formatConfirmedAt(confirmation!.confirmedAt)} ·{' '}
                {CLIENT_LOCATION_SOURCE_LABELS[confirmation!.source]}
              </Text>
              {confirmation!.stateMismatch ? (
                <Alert color="yellow" variant="light" p="xs" icon={<IconAlertTriangle size={14} />}>
                  <Text size="xs">
                    Confirmed state differs from the device location estimate. Review if needed.
                  </Text>
                </Alert>
              ) : null}
              {confirmation!.licensureBlocking ? (
                <Alert color="red" variant="light" p="xs" icon={<IconAlertTriangle size={14} />}>
                  <Text size="xs">Location may not be supported for this session.</Text>
                </Alert>
              ) : null}
            </Stack>
          ) : (
            <Text size="xs" c="dimmed">
              Enter the client&apos;s session location if not yet confirmed.
            </Text>
          )}

          <form
            onSubmit={form.onSubmit(async (values) => {
              await onSave(patientId, values);
            })}
          >
            <Stack gap="xs">
              <TextInput
                label="Street address"
                size="xs"
                required
                disabled={saving}
                styles={{ label: { marginBottom: 2, fontSize: 'var(--mantine-font-size-xs)' } }}
                {...form.getInputProps('street')}
              />
              <TextInput
                label="City"
                size="xs"
                required
                disabled={saving}
                styles={{ label: { marginBottom: 2, fontSize: 'var(--mantine-font-size-xs)' } }}
                {...form.getInputProps('city')}
              />
              <UsStateSelect
                label="State"
                size="xs"
                required
                disabled={saving}
                comboboxProps={sessionSidebarComboboxProps()}
                styles={{ label: { marginBottom: 2, fontSize: 'var(--mantine-font-size-xs)' } }}
                value={form.values.state}
                onChange={(v) => form.setFieldValue('state', v)}
                error={form.errors.state}
              />
              <TextInput
                label="ZIP code"
                size="xs"
                required
                disabled={saving}
                styles={{ label: { marginBottom: 2, fontSize: 'var(--mantine-font-size-xs)' } }}
                {...form.getInputProps('postalCode')}
              />

              {saveError ? (
                <Alert color="red" variant="light" p="xs">
                  <Text size="xs">{saveError}</Text>
                </Alert>
              ) : null}

              <Button type="submit" size="xs" variant="light" loading={saving}>
                Save location
              </Button>
            </Stack>
          </form>
        </Stack>
      </Collapse>
    </Box>
  );
}
